import type { SiteName } from '@seek/melways-sites';
import type { ParsedFormatFnByKey as FormatFnByKey } from '@vocab/core';
import type { useTranslations } from '@vocab/react';
import {
  IconExperience,
  IconRecommended,
  IconRocket,
  IconSearch,
  Strong,
  Text,
} from 'braid-design-system';

// Hacky type until refactor
type TType<FFBK extends FormatFnByKey> = ReturnType<
  typeof useTranslations<string, FFBK>
>['t'];

/**
 * Builds the default site config (AU/NZ, etc.)
 */
const buildDefaultConfig = (
  t: TType<FormatFnByKey>,
): JobAdFeatureChartSiteConfig => ({
  headerPlan: [
    {
      label: t('Basic'),
      icon: <IconRecommended tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          {t(
            'Offers a balance between performance and cost with exposure to relevant candidates - ideal for',
          )}{' '}
          <Strong>{t('easy-to-fill roles')}</Strong>
        </Text>
      ),
      text: t(
        'Offers a balance between performance and cost with exposure to relevant candidates - ideal for easy-to-fill roles',
      ),
    },
    {
      label: t('Advanced'),
      icon: <IconRocket tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          {t(
            'Offers a balance between performance and cost with exposure to relevant candidates - ideal for',
          )}{' '}
          <Strong>{t('easy-to-fill roles')}</Strong>
        </Text>
      ),
      text: t(
        'Offers a balance between performance and cost with exposure to relevant candidates - ideal for easy-to-fill roles',
      ),
    },
    {
      label: t('Premium'),
      icon: <IconExperience tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          {t(
            'Our top-performing ad, with maximum exposure and features to help you stand out - designed for',
          )}{' '}
          <Strong>{t('hard-to-fill, critical and urgent roles')}</Strong>
        </Text>
      ),
      text: t(
        'Our top-performing ad, with maximum exposure and features to help you stand out - designed for hard-to-fill, critical and urgent roles.',
      ),
    },
  ],
  jobAdPlanInfo: [
    {
      title: t('Exposure in search and recommendations'),
      data: [
        { type: 'badge', info: 'Standard' },
        { type: 'badge', info: 'Promoted' },
        { type: 'badge', info: 'Prioritised' },
      ],
    },
    {
      title: t('Candidate invitations'),
      data: [
        { type: 'badge', info: '20' },
        { type: 'badge', info: '30' },
        { type: 'badge', info: '80' },
      ],
    },
    {
      title: t('Enhanced targeting'),
      data: [
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
    {
      title: t('Promoted on competitive ads'),
      data: [
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
    {
      title: t('Maintain exclusivity on your ad'),
      data: [
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
    {
      title: t('Your ad sent exclusively to high-fit candidates'),
      data: [
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
    {
      title: t('Branding'),
      data: [
        { type: 'icon', info: t('Optional'), infoEn: 'optional' },
        { type: 'icon', info: t('Optional'), infoEn: 'optional' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
  ],
  colNum: 4,
});

/**
 * Builds the Asia-specific site config
 */
const buildAsiaConfig = (
  t: TType<FormatFnByKey>,
): JobAdFeatureChartSiteConfig => ({
  headerPlan: [
    {
      label: t('Lite'),
      icon: <IconSearch tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          {t('Appears below paid ads in search results - for hiring where')}{' '}
          <Strong>{t('cost-efficiency matters most')}</Strong>
        </Text>
      ),
      text: t(
        'Appears below paid ads in search results - for hiring where cost-efficiency matters most',
      ),
    },
    {
      label: t('Basic'),
      icon: <IconRecommended tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          {t('Standard visibility in search and recommendations - for')}{' '}
          <Strong>{t('general hiring needs')}</Strong>
        </Text>
      ),
      text: t(
        'Standard visibility in search and recommendations - for general hiring needs',
      ),
    },
    {
      label: t('Advanced'),
      icon: <IconRocket tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          {t('Enhanced targeting to high fit candidates - for')}{' '}
          <Strong>{t('hard-to-fill and competitive roles')}</Strong>
        </Text>
      ),
      text: t(
        'Enhanced targeting to high fit candidates - for hard-to-fill and competitive roles',
      ),
    },
    {
      label: t('Premium'),
      icon: <IconExperience tone="brandAccent" />,
      textSpan: (
        <Text size="xsmall">
          {t('Maximum performance and premium features - for')}{' '}
          <Strong>{t('urgent, critical and high-impact hiring')}</Strong>
        </Text>
      ),
      text: t(
        'Maximum performance and premium features - for urgent, critical and high-impact hiring',
      ),
    },
  ],
  jobAdPlanInfo: [
    {
      title: t('Exposure in search and recommendations'),
      data: [
        { type: 'badge', info: t('Limited') },
        { type: 'badge', info: t('Standard') },
        { type: 'badge', info: t('Promoted') },
        { type: 'badge', info: t('Prioritised') },
      ],
    },
    {
      title: t('Candidate invitations'),
      data: [
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'badge', info: '20' },
        { type: 'badge', info: '30' },
        { type: 'badge', info: '80' },
      ],
    },
    {
      title: t('Enhanced targeting'),
      data: [
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
    {
      title: t('Promoted on competitive ads'),
      data: [
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
    {
      title: t('Maintain exclusivity on your ad'),
      data: [
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
    {
      title: t('Your ad sent exclusively to high-fit candidates'),
      data: [
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('no'), infoEn: 'no' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
    {
      title: t('Branding'),
      data: [
        { type: 'icon', info: t('Optional'), infoEn: 'optional' },
        { type: 'icon', info: t('Optional'), infoEn: 'optional' },
        { type: 'icon', info: t('Optional'), infoEn: 'optional' },
        { type: 'icon', info: t('yes'), infoEn: 'yes' },
      ],
    },
  ],
  colNum: 5,
});

/**
 * Returns the correct config based on `site`
 */
export const jobAdFeatureChartData = (
  site: SiteName,
  t: TType<FormatFnByKey>,
) => {
  switch (site) {
    case 'employer-seek-hk':
    case 'employer-seek-sg':
    case 'employer-seek-my':
    case 'employer-seek-id':
    case 'employer-seek-th':
    case 'employer-seek-ph':
      return buildAsiaConfig(t);
    default:
      return buildDefaultConfig(t);
  }
};

// Types
export interface JobAdFeatureChartSiteConfig {
  headerPlan: Array<{
    label: string;
    icon: React.ReactNode;
    textSpan: React.ReactNode;
    text: string;
  }>;
  jobAdPlanInfo: Array<{
    title: string;
    data: JobAdPlanDataItem[];
  }>;
  colNum: 4 | 5;
}

export type JobAdPlanDataItem =
  | { type: 'badge'; info: string | number }
  | { type: 'icon'; info: string; infoEn: string | number };
